import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useCommon } from '../../../../contexts/CommonContext';
import { useTenant } from '../../../../contexts/TenantContext';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import PageContainer from '../../../../components/PageContainer/PageContainer';
import { KnowledgeBaseApi } from '../../../../api/KnowledgeBaseApi';
import { showAlert, showToast } from '../../../../api/CommonApi';
import { Document, DocumentStatus } from '../../../../models/knowledgeBase/Document';
import {
  BdsDropdown,
  BdsGrid,
  BdsIcon,
  BdsList,
  BdsListItem,
  BdsSkeleton,
  BdsSwitch,
  BdsTableCell,
  BdsTableRow,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { usePaginatedSearch } from '../../../../hooks/usePaginatedSearch';
import { useTableStates } from '../../../../hooks/useTableStates';
import { useBreadcrumb } from '../../../../hooks/useBreadcrumb';
import { useLocalizedBreadcrumbs } from '../../../../utils/breadcrumbUtils';
import { localization } from './localization';
import { calculateOffset } from '../../helper';
import { MoreOptionsButton, SearchInput } from './styles';
import UrlPathDetailSidebar from './UrlPathDetailSidebar';

// Constants
const PAGE_OPTIONS = [5, 10, 15];

type DeleteVersionAlertResponse = { response: boolean };

// Location state interface for data passed from Documents.tsx
interface LocationState {
  catalogName?: string;
  documentName?: string;
}

const UrlPaths: React.FC = () => {
  // Router and context hooks
  const { catalogId, documentId } = useParams<{ catalogId: string; documentId: string }>();
  const location = useLocation<LocationState>();
  const { setHeaderContent } = useCommon();
  const { tenant } = useTenant();
  const translate = useContentLocalizer(localization);

  // Custom hooks
  const {
    searchTerm,
    debouncedSearchTerm,
    page,
    itemsPerPage,
    loadingState,
    setLoadingState,
    handleSearchChange,
    handlePaginationChange,
    handleItemsPerPageChange,
  } = usePaginatedSearch({ pageOptions: PAGE_OPTIONS });

  // Computed values
  const { isLoading, hasError } = loadingState;
  const tenantId = tenant?.info?.id;

  // State management
  const [urlPathDetailSidebarIsOpen, seturlPathDetailSidebarIsOpen] = useState(false);
  const [openedUrlPath, setOpenedUrlPath] = useState<Document>();
  const [versions, setVersions] = useState<Document[]>([]);
  const [totalVersions, setTotalVersions] = useState(0);

  // Ref to track ongoing operations and prevent duplicate calls
  const operationsInProgressRef = useRef<Set<string>>(new Set());
  const dropdownRefs = useRef<Map<string, HTMLBdsDropdownElement>>(new Map());
  const [catalogName, setCatalogName] = useState(location.state?.catalogName ?? '');
  const [documentName, setDocumentName] = useState(location.state?.documentName ?? '');
  const [isCatalogAndDocumentInfoLoading, setIsCatalogAndDocumentInfoLoading] = useState(
    !location.state?.catalogName || !location.state?.documentName,
  );

  // Memoized values
  const skeletonElement = useMemo(() => <BdsSkeleton width="120px" height="16px" />, []);

  // Breadcrumb management
  const breadcrumbData = useLocalizedBreadcrumbs('knowledgeBaseUrlPaths', {
    catalogName,
    catalogId,
    documentName,
    isLoading: isCatalogAndDocumentInfoLoading,
    skeletonElement,
  });
  const { setItems: setBreadcrumbItems } = useBreadcrumb([]);

  // Table states for proper empty state handling
  const computedStates = useTableStates({
    loadingState,
    itemsLength: versions.length,
    searchTermLength: debouncedSearchTerm.length,
  });

  // API handlers
  const fetchVersions = useCallback(async (): Promise<void> => {
    if (!tenantId || !catalogId || !documentId || !itemsPerPage || !page) return;

    setLoadingState({ isLoading: true, hasError: false });
    try {
      const data = await KnowledgeBaseApi.getDocumentVersionsAsync(
        tenantId,
        catalogId,
        documentId,
        itemsPerPage,
        calculateOffset(page, itemsPerPage),
        debouncedSearchTerm || undefined,
      );
      setVersions(data.items);
      setTotalVersions(data.total);
      setLoadingState({ isLoading: false, hasError: false });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching versions:', error);
      setLoadingState({ isLoading: false, hasError: true });
    }
  }, [tenantId, catalogId, documentId, itemsPerPage, page, debouncedSearchTerm]);

  const fetchCatalogAndDocumentInfo = useCallback(async (): Promise<void> => {
    if (!tenantId || !catalogId || !documentId) return;

    const hasCatalogName = !!location.state?.catalogName;
    const hasDocumentName = !!location.state?.documentName;

    // If we have all the info from location state, no need to fetch
    if (hasCatalogName && hasDocumentName) {
      setIsCatalogAndDocumentInfoLoading(false);
      return;
    }

    setIsCatalogAndDocumentInfoLoading(true);

    try {
      // Fetch missing data in parallel using object destructuring for cleaner result handling
      const [catalog, document] = await Promise.all([
        hasCatalogName
          ? Promise.resolve({ name: catalogName })
          : KnowledgeBaseApi.getCatalogByIdAsync(tenantId, catalogId),
        hasDocumentName
          ? Promise.resolve({ name: documentName })
          : KnowledgeBaseApi.getDocumentByIdAsync(tenantId, catalogId, documentId),
      ]);

      setCatalogName(catalog.name);
      setDocumentName(document.name);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching catalog and document info:', error);
    } finally {
      setIsCatalogAndDocumentInfoLoading(false);
    }
  }, [
    tenantId,
    catalogId,
    documentId,
    location.state?.catalogName,
    location.state?.documentName,
    catalogName,
    documentName,
  ]);

  // Alert helpers
  const showDeleteVersionAlert = useCallback(
    (): Promise<DeleteVersionAlertResponse> =>
      showAlert({
        ...translate.deleteVersionAlert,
        variant: 'delete',
        icon: 'trash',
      }),
    [translate],
  );

  // Event handlers
  const handleSynchronizationToggle = useCallback(
    async (version: Document) => {
      const operationKey = `toggle-${version.version_id}`;

      // Prevent duplicate calls using ref
      if (operationsInProgressRef.current.has(operationKey)) {
        return;
      }

      operationsInProgressRef.current.add(operationKey);

      const newStatus = version.status === DocumentStatus.ACTIVE ? DocumentStatus.INACTIVE : DocumentStatus.ACTIVE;

      try {
        await KnowledgeBaseApi.updateDocumentVersionAsync(tenantId, catalogId, documentId, version.version_id, {
          status: newStatus,
        });

        showToast({
          ...translate.successUpdateVersionStatusToast,
          type: 'success',
        });

        // Update the opened URL path with the new status if it's the same document
        setOpenedUrlPath((prev) => {
          if (prev && prev.version_id === version.version_id) {
            return {
              ...prev,
              status: newStatus,
            };
          }
          return prev;
        });

        fetchVersions();
      } catch (error) {
        showToast({
          ...translate.errorUpdateVersionStatusToast,
          type: 'danger',
        });
      } finally {
        operationsInProgressRef.current.delete(operationKey);
      }
    },
    [tenantId, catalogId, documentId, fetchVersions, translate],
  );

  const handleDeleteVersion = useCallback(
    async (version: Document) => {
      const operationKey = `delete-${version.version_id}`;

      // Prevent duplicate calls
      if (operationsInProgressRef.current.has(operationKey)) {
        return;
      }

      const confirm = await showDeleteVersionAlert();
      if (!confirm.response) return;

      operationsInProgressRef.current.add(operationKey);

      try {
        setLoadingState((prev) => ({ ...prev, isLoading: true }));
        await KnowledgeBaseApi.deleteDocumentVersionAsync(tenantId, catalogId, documentId, version.version_id);

        showToast({
          ...translate.successDeleteVersionToast,
          type: 'success',
        });

        fetchVersions();
      } catch (error) {
        setLoadingState((prev) => ({ ...prev, isLoading: false }));
        showToast({
          ...translate.errorDeleteVersionToast,
          type: 'danger',
        });
      } finally {
        operationsInProgressRef.current.delete(operationKey);
        seturlPathDetailSidebarIsOpen(false);
      }
    },
    [tenantId, catalogId, documentId, fetchVersions, showDeleteVersionAlert, translate],
  );

  const handleTitleEdit = useCallback(
    async (version: Document, newTitle: string) => {
      if (!newTitle.trim() || newTitle === version.name) {
        return;
      }

      const operationKey = `edit-${version.version_id}`;

      // Prevent duplicate calls using ref
      if (operationsInProgressRef.current.has(operationKey)) {
        return;
      }

      operationsInProgressRef.current.add(operationKey);

      try {
        await KnowledgeBaseApi.updateDocumentVersionAsync(tenantId, catalogId, documentId, version.version_id, {
          name: newTitle.trim(),
        });

        showToast({
          ...translate.successUpdateVersionNameToast,
          type: 'success',
        });

        // Update the opened URL path with the new name
        setOpenedUrlPath((prev) => {
          if (prev && prev.version_id === version.version_id) {
            return {
              ...prev,
              name: newTitle.trim(),
            };
          }
          return prev;
        });

        fetchVersions();
      } catch (error) {
        showToast({
          ...translate.errorUpdateVersionNameToast,
          type: 'danger',
        });
      } finally {
        operationsInProgressRef.current.delete(operationKey);
      }
    },
    [tenantId, catalogId, documentId, fetchVersions, translate],
  );

  // Render helpers
  const renderVersionRow = useCallback(
    (item: Document) => {
      const closeDropdown = () => {
        const dropdown = dropdownRefs.current.get(item.version_id);
        if (dropdown && typeof dropdown.setClose === 'function') {
          dropdown.setClose();
        }
      };

      const setDropdownRef = (element: HTMLBdsDropdownElement | null) => {
        if (element) {
          dropdownRefs.current.set(item.version_id, element);
        } else {
          dropdownRefs.current.delete(item.version_id);
        }
      };

      return (
        <BdsTableRow key={item.version_id}>
          <BdsTableCell>
            <BdsGrid direction="row" align-items="center" gap="2">
              <BdsIcon name="site" />
              <BdsTypo variant="fs-14" bold="bold">
                {item.name}
              </BdsTypo>
            </BdsGrid>
          </BdsTableCell>
          <BdsTableCell>
            <BdsSwitch
              name={`version-${item.version_id}`}
              refer=""
              checked={item.status === DocumentStatus.ACTIVE}
              onBdsChange={() => handleSynchronizationToggle(item)}
            />
          </BdsTableCell>
          <BdsTableCell>
            <BdsDropdown
              ref={setDropdownRef}
              position="bottom-left"
              onBdsToggle={(event: CustomEvent) => {
                // Ensure dropdown state is managed properly
                if (!event.detail.value) {
                  // Dropdown is closing, no additional action needed
                }
              }}
            >
              <div slot="dropdown-activator">
                <MoreOptionsButton name="more-options-vertical" />
              </div>
              <div slot="dropdown-content">
                <BdsList type-list="default">
                  <BdsListItem
                    text={translate.urlPathMoreOptions.view}
                    clickable
                    icon="file-txt"
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      event.stopPropagation();
                      closeDropdown();
                      seturlPathDetailSidebarIsOpen(true);
                      setOpenedUrlPath(item);
                    }}
                  />
                  <BdsListItem
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      event.stopPropagation();
                      closeDropdown();
                      handleDeleteVersion(item);
                    }}
                    text={translate.urlPathMoreOptions.delete}
                    clickable
                    icon="trash"
                  />
                </BdsList>
              </div>
            </BdsDropdown>
          </BdsTableCell>
        </BdsTableRow>
      );
    },
    [handleSynchronizationToggle, handleDeleteVersion],
  );

  // Effects
  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  useEffect(() => {
    fetchCatalogAndDocumentInfo();
  }, [fetchCatalogAndDocumentInfo]);

  useEffect(() => {
    // Set breadcrumbs using the utility-generated data
    if (breadcrumbData?.breadcrumbItems) {
      setBreadcrumbItems(breadcrumbData.breadcrumbItems);

      // Pass breadcrumb data directly to setHeaderContent (same format as Documents.tsx)
      setHeaderContent({
        breadcrumbItems: breadcrumbData.breadcrumbItems,
        lastItemEditable: breadcrumbData.lastItemEditable,
        onEditLastItem: breadcrumbData.onEditLastItem,
      });
    }
    return () => setHeaderContent({ redirect: null });
  }, [breadcrumbData, setBreadcrumbItems, setHeaderContent]);

  // Early return for missing tenant
  if (!tenantId) return null;

  return (
    <PageContainer>
      <BdsGrid direction="column" gap="4">
        <PageHeader title={translate.title} subtitle={translate.subtitle} />

        <SearchInput
          onBdsChange={handleSearchChange}
          icon="search"
          placeholder={translate.searchPlaceholder}
          value={searchTerm}
        />

        <TableWithPagination
          options={{
            heading: [
              translate.tableHeaders.pages,
              translate.tableHeaders.synchronization,
              translate.tableHeaders.actions,
            ],
            itemsPerPageList: PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: totalVersions,
            isLoading,
            hasLoadingError: hasError,
            hasEmptyState: computedStates.hasNoItems || computedStates.hasSearchResults,
          }}
          onRefreshData={fetchVersions}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {versions.length > 0 && versions.map(renderVersionRow)}
        </TableWithPagination>
      </BdsGrid>
      {openedUrlPath && (
        <UrlPathDetailSidebar
          isOpen={urlPathDetailSidebarIsOpen}
          tenantId={tenantId}
          urlPath={openedUrlPath}
          onDelete={handleDeleteVersion}
          onTitleEdit={handleTitleEdit}
          onClose={() => seturlPathDetailSidebarIsOpen(false)}
        />
      )}
    </PageContainer>
  );
};

export default UrlPaths;
